/**
 * User projection — Cosmos `users` container.
 *
 * Partition key: `/id`. One doc per user.
 *
 * Entra ID is the source of truth for users + role assignments. This
 * container is a *cached projection* updated by the admin login flow
 * (Phase 6) so the admin UI can render lists, GDPR-deletion targets,
 * and the audit log's user references without round-tripping to Entra
 * on every page load.
 *
 * Sensitive fields (passwords, MFA secrets, refresh tokens) are NEVER
 * stored here — Entra handles that. We only mirror the public-facing
 * identity + role assignment.
 */

import { z } from 'zod';
import {
  CosmosBaseSchema,
  IsoTimestampSchema,
  RoleSchema,
} from './common.js';

export const UserSchema = CosmosBaseSchema.extend({
  docType: z.literal('user'),
  email: z.string().email(),
  displayName: z.string().min(1).max(256),
  role: RoleSchema,
  /** ISO 8601 timestamp of last successful login. */
  lastLogin: IsoTimestampSchema.optional(),
  /** Account is administratively disabled (set via Entra; mirrored here). */
  disabled: z.boolean(),
}).strict();
export type User = z.infer<typeof UserSchema>;
